/**
 * useAutoGeneration Hook
 * 
 * 管理项目设定自动生成的状态和 WebSocket 消息处理
 * 
 * @module hooks/useAutoGeneration
 * @see Requirements 4.5, 9.3, 2.7
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  GenerationStepType,
  ProjectSettingStepStatus,
  AutoGenerationStatusResponse,
  GENERATION_STEP_ORDER,
  GENERATION_STEP_NAMES,
  TOTAL_GENERATION_STEPS,
  getLabelsByLocale,
  Locale
} from '@packages/global';
import i18n from '../i18n';
import { useWebSocketContext } from '../services/websocket/WebSocketContext';
import { projectSettingGenerationApi } from '../services/user/projectSettingGeneration.api';
import { MessageType } from '../services/websocket/types';
import type {
  ProjectSettingStepStartedMessage,
  ProjectSettingStepProgressMessage,
  ProjectSettingStepCompletedMessage,
  ProjectSettingStepFailedMessage,
  ProjectMetaUpdatedMessage,
  EpisodeScriptProgressMessage
} from '../services/websocket/types';

/**
 * 单个步骤的状态
 */
export interface StepState {
  type: GenerationStepType;
  name: string;
  status: ProjectSettingStepStatus;
  content: string;
  error?: string;
}

/**
 * 单集剧本生成进度
 */
export interface EpisodeScriptProgress {
  episodeIndex: number;
  title: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  streamingContent?: string;
  errorMessage?: string;
}

/**
 * 自动生成状态
 */
export interface AutoGenerationState {
  isActive: boolean;
  currentStep: GenerationStepType | null;
  completedSteps: GenerationStepType[];
  failedStep: GenerationStepType | null;
  errorMessage: string | null;
  scriptIdea: string;
  novelFileUrl?: string;  // 新增：小说文件 URL
  steps: StepState[];
  streamingContent: Record<string, string>;
  projectMeta: {
    title: string;
    description: string;
  } | null;
  // 新增：分集剧本生成进度
  episodeScriptProgress: {
    totalEpisodes: number;
    completedEpisodes: number;
    currentEpisode: number | null;
    failedEpisodes: number[];
    episodes: EpisodeScriptProgress[];
  } | null;
}

/**
 * Hook 返回类型
 */
export interface UseAutoGenerationReturn {
  state: AutoGenerationState;
  isLoading: boolean;
  progress: number;
  currentStepName: string | null;
  retry: (stepType: GenerationStepType) => Promise<void>;
  cancel: () => Promise<void>;
  switchToManualMode: () => Promise<void>;
  refresh: () => Promise<void>;
  getStepStatus: (stepType: GenerationStepType) => ProjectSettingStepStatus;
  isStepLocked: (stepType: GenerationStepType) => boolean;
}

/**
 * Hook 配置选项
 */
export interface UseAutoGenerationOptions {
  onProjectMetaUpdated?: (title: string, description: string) => void;
  onStepCompleted?: (stepType: GenerationStepType) => void;
}

/**
 * 创建初始状态
 */
const createInitialState = (): AutoGenerationState => ({
  isActive: false,
  currentStep: null,
  completedSteps: [],
  failedStep: null,
  errorMessage: null,
  scriptIdea: '',
  novelFileUrl: undefined,
  steps: GENERATION_STEP_ORDER.map(type => ({
    type,
    name: getLabelsByLocale(GENERATION_STEP_NAMES, i18n.language as Locale)[type],
    status: ProjectSettingStepStatus.PENDING,
    content: ''
  })),
  streamingContent: {},
  projectMeta: null,
  episodeScriptProgress: null
});

/**
 * useAutoGeneration Hook
 * 
 * @param projectId - 项目 ID
 * @param options - 配置选项
 */
export function useAutoGeneration(
  projectId: string | undefined,
  options?: UseAutoGenerationOptions
): UseAutoGenerationReturn {
  const { onProjectMetaUpdated, onStepCompleted } = options || {};
  const { client, isConnected } = useWebSocketContext();
  const [state, setState] = useState<AutoGenerationState>(createInitialState());
  const [isLoading, setIsLoading] = useState(false);
  const subscribedRef = useRef(false);
  // 记录上一次的 completedSteps，用于检测新完成的步骤
  const prevCompletedStepsRef = useRef<GenerationStepType[]>([]);
  // 使用 ref 存储回调，避免闭包过期问题
  const onStepCompletedRef = useRef(onStepCompleted);
  onStepCompletedRef.current = onStepCompleted;
  const onProjectMetaUpdatedRef = useRef(onProjectMetaUpdated);
  onProjectMetaUpdatedRef.current = onProjectMetaUpdated;

  /**
   * 计算进度百分比
   */
  const progress = state.completedSteps.length / TOTAL_GENERATION_STEPS * 100;

  /**
   * 获取当前步骤名称
   */
  const currentStepName = state.currentStep
    ? getLabelsByLocale(GENERATION_STEP_NAMES, i18n.language as Locale)[state.currentStep]
    : null;

  /**
   * 获取步骤状态
   */
  const getStepStatus = useCallback((stepType: GenerationStepType): ProjectSettingStepStatus => {
    if (state.completedSteps.includes(stepType)) {
      return ProjectSettingStepStatus.COMPLETED;
    }
    if (state.failedStep === stepType) {
      return ProjectSettingStepStatus.FAILED;
    }
    if (state.currentStep === stepType) {
      return ProjectSettingStepStatus.IN_PROGRESS;
    }
    return ProjectSettingStepStatus.PENDING;
  }, [state.completedSteps, state.failedStep, state.currentStep]);

  /**
   * 判断步骤是否锁定（自动生成模式下，未完成的步骤不可点击）
   */
  const isStepLocked = useCallback((stepType: GenerationStepType): boolean => {
    if (!state.isActive) return false;
    return !state.completedSteps.includes(stepType);
  }, [state.isActive, state.completedSteps]);

  /**
   * 从服务器获取状态
   */
  const fetchStatus = useCallback(async () => {
    if (!projectId) return;

    try {
      const status = await projectSettingGenerationApi.getStatus(projectId);

      console.log('[useAutoGeneration] fetchStatus response:', {
        isActive: status.isActive,
        currentStep: status.currentStep,
        completedSteps: status.completedSteps,
        failedStep: status.failedStep
      });

      // 检测新完成的步骤
      const newCompletedSteps = (status.completedSteps || []).filter(
        step => !prevCompletedStepsRef.current.includes(step)
      );

      // 触发步骤完成回调
      if (newCompletedSteps.length > 0 && onStepCompletedRef.current) {
        newCompletedSteps.forEach(step => onStepCompletedRef.current!(step));
      }

      // 更新记录
      prevCompletedStepsRef.current = status.completedSteps || [];

      setState(prev => ({
        ...prev,
        isActive: status.isActive,
        currentStep: status.currentStep,
        completedSteps: status.completedSteps || [],
        failedStep: status.failedStep,
        errorMessage: status.errorMessage,
        scriptIdea: status.scriptIdea,
        novelFileUrl: status.novelFileUrl,  // 新增
        episodeScriptProgress: status.episodeScriptProgress ? {
          totalEpisodes: status.episodeScriptProgress.totalEpisodes,
          completedEpisodes: status.episodeScriptProgress.completedEpisodes,
          currentEpisode: status.episodeScriptProgress.currentEpisode,
          failedEpisodes: status.episodeScriptProgress.failedEpisodes,
          episodes: Array.from({ length: status.episodeScriptProgress.totalEpisodes }, (_, i) => {
            const episodeIndex = i + 1;
            const isCompleted = episodeIndex <= status.episodeScriptProgress!.completedEpisodes;
            const isFailed = status.episodeScriptProgress!.failedEpisodes.includes(episodeIndex);
            const isCurrent = episodeIndex === status.episodeScriptProgress!.currentEpisode;
            return {
              episodeIndex,
              title: '',
              status: isFailed ? 'failed' as const : isCompleted ? 'completed' as const : isCurrent ? 'in_progress' as const : 'pending' as const,
              streamingContent: '',
              errorMessage: undefined
            };
          })
        } : prev.episodeScriptProgress,
        steps: prev.steps.map(step => ({
          ...step,
          status: (status.completedSteps || []).includes(step.type)
            ? ProjectSettingStepStatus.COMPLETED
            : status.failedStep === step.type
              ? ProjectSettingStepStatus.FAILED
              : status.currentStep === step.type
                ? ProjectSettingStepStatus.IN_PROGRESS
                : ProjectSettingStepStatus.PENDING
        }))
      }));
    } catch (error) {
      console.error('[useAutoGeneration] Failed to fetch status:', error);
    }
  }, [projectId]);

  /**
   * 刷新状态
   */
  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      await fetchStatus();
    } finally {
      setIsLoading(false);
    }
  }, [fetchStatus]);

  /**
   * 重试失败步骤
   */
  const retry = useCallback(async (stepType: GenerationStepType) => {
    if (!projectId) return;

    setIsLoading(true);
    try {
      await projectSettingGenerationApi.retryStep(projectId, stepType);
      setState(prev => ({
        ...prev,
        isActive: true,
        currentStep: stepType,
        failedStep: null,
        errorMessage: null
      }));
    } catch (error: any) {
      console.error('[useAutoGeneration] Retry failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  /**
   * 取消自动生成
   */
  const cancel = useCallback(async () => {
    if (!projectId) return;

    setIsLoading(true);
    try {
      await projectSettingGenerationApi.cancelAutoGeneration(projectId);
      setState(prev => ({
        ...prev,
        isActive: false,
        currentStep: null
      }));
    } catch (error: any) {
      console.error('[useAutoGeneration] Cancel failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  /**
   * 切换到手动模式
   */
  const switchToManualMode = useCallback(async () => {
    if (!projectId) return;

    setIsLoading(true);
    try {
      await projectSettingGenerationApi.switchToManualMode(projectId);
      setState(prev => ({
        ...prev,
        isActive: false,
        currentStep: null,
        failedStep: null,
        errorMessage: null
      }));
    } catch (error: any) {
      console.error('[useAutoGeneration] Switch to manual mode failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  // 处理步骤开始消息
  const handleStepStarted = useCallback((message: ProjectSettingStepStartedMessage) => {
    if (message.projectId !== projectId) return;

    setState(prev => ({
      ...prev,
      isActive: true,
      currentStep: message.stepType,
      steps: prev.steps.map(step =>
        step.type === message.stepType
          ? { ...step, status: ProjectSettingStepStatus.IN_PROGRESS }
          : step
      )
    }));
  }, [projectId]);

  // 处理步骤进度消息（流式内容）
  const handleStepProgress = useCallback((message: ProjectSettingStepProgressMessage) => {
    if (message.projectId !== projectId) return;

    setState(prev => ({
      ...prev,
      streamingContent: {
        ...prev.streamingContent,
        [message.stepType]: message.fullContent
      }
    }));
  }, [projectId]);

  // 处理步骤完成消息
  const handleStepCompleted = useCallback((message: ProjectSettingStepCompletedMessage) => {
    if (message.projectId !== projectId) return;

    setState(prev => ({
      ...prev,
      completedSteps: [...prev.completedSteps, message.stepType],
      steps: prev.steps.map(step =>
        step.type === message.stepType
          ? { ...step, status: ProjectSettingStepStatus.COMPLETED, content: message.content }
          : step
      ),
      streamingContent: {
        ...prev.streamingContent,
        [message.stepType]: ''
      }
    }));
  }, [projectId]);

  // 处理步骤失败消息
  const handleStepFailed = useCallback((message: ProjectSettingStepFailedMessage) => {
    if (message.projectId !== projectId) return;

    setState(prev => ({
      ...prev,
      isActive: false,
      failedStep: message.stepType,
      errorMessage: message.error,
      currentStep: null,
      steps: prev.steps.map(step =>
        step.type === message.stepType
          ? { ...step, status: ProjectSettingStepStatus.FAILED, error: message.error }
          : step
      )
    }));
  }, [projectId]);

  // 处理项目信息更新消息
  const handleProjectMetaUpdated = useCallback((message: ProjectMetaUpdatedMessage) => {
    if (message.projectId !== projectId) return;

    setState(prev => ({
      ...prev,
      projectMeta: {
        title: message.title,
        description: message.description
      }
    }));

    // 通知外部更新项目信息（通过 ref 避免依赖外部回调引用）
    onProjectMetaUpdatedRef.current?.(message.title, message.description);
  }, [projectId]);

  // 处理分集剧本生成进度消息
  const handleEpisodeScriptProgress = useCallback((message: EpisodeScriptProgressMessage) => {
    if (message.projectId !== projectId) return;

    setState(prev => {
      const currentProgress = prev.episodeScriptProgress || {
        totalEpisodes: message.totalEpisodes,
        completedEpisodes: 0,
        currentEpisode: null,
        failedEpisodes: [],
        episodes: Array.from({ length: message.totalEpisodes }, (_, i) => ({
          episodeIndex: i + 1,
          title: '',
          status: 'pending' as const,
          streamingContent: '',
          errorMessage: undefined
        }))
      };

      const updatedEpisodes = currentProgress.episodes.map(ep => {
        if (ep.episodeIndex !== message.episodeIndex) return ep;

        if (message.status === 'started') {
          return { ...ep, status: 'in_progress' as const, streamingContent: '' };
        }
        if (message.status === 'in_progress') {
          return { ...ep, status: 'in_progress' as const, streamingContent: message.fullContent || '' };
        }
        if (message.status === 'completed') {
          return { ...ep, status: 'completed' as const, streamingContent: '' };
        }
        if (message.status === 'failed') {
          return { ...ep, status: 'failed' as const, errorMessage: message.errorMessage };
        }
        return ep;
      });

      const completedCount = updatedEpisodes.filter(ep => ep.status === 'completed').length;
      const failedIndices = updatedEpisodes
        .filter(ep => ep.status === 'failed')
        .map(ep => ep.episodeIndex);

      return {
        ...prev,
        episodeScriptProgress: {
          totalEpisodes: message.totalEpisodes,
          completedEpisodes: completedCount,
          currentEpisode: message.status === 'in_progress' ? message.episodeIndex : null,
          failedEpisodes: failedIndices,
          episodes: updatedEpisodes
        }
      };
    });
  }, [projectId]);

  // 注册 WebSocket 消息处理器
  useEffect(() => {
    if (!client || !isConnected || !projectId) return;

    const unsubscribers = [
      client.onMessage(MessageType.PROJECT_SETTING_STEP_STARTED, handleStepStarted),
      client.onMessage(MessageType.PROJECT_SETTING_STEP_PROGRESS, handleStepProgress),
      client.onMessage(MessageType.PROJECT_SETTING_STEP_COMPLETED, handleStepCompleted),
      client.onMessage(MessageType.PROJECT_SETTING_STEP_FAILED, handleStepFailed),
      client.onMessage(MessageType.PROJECT_SETTING_PROJECT_META_UPDATED, handleProjectMetaUpdated),
      client.onMessage(MessageType.PROJECT_SETTING_EPISODE_SCRIPT_PROGRESS, handleEpisodeScriptProgress)
    ];

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [
    client,
    isConnected,
    projectId,
    handleStepStarted,
    handleStepProgress,
    handleStepCompleted,
    handleStepFailed,
    handleProjectMetaUpdated,
    handleEpisodeScriptProgress
  ]);

  // 初始化时获取状态
  useEffect(() => {
    if (projectId) {
      fetchStatus();
    }
  }, [projectId, fetchStatus]);

  // 轮询机制：当自动生成进行中时，定期获取状态
  // 因为 Worker 进程中 WebSocket 广播不工作，需要通过轮询获取最新状态
  useEffect(() => {
    if (!projectId || !state.isActive) return;

    const pollInterval = setInterval(() => {
      fetchStatus();
    }, 2000); // 每 2 秒轮询一次

    return () => clearInterval(pollInterval);
  }, [projectId, state.isActive, fetchStatus]);

  return {
    state,
    isLoading,
    progress,
    currentStepName,
    retry,
    cancel,
    switchToManualMode,
    refresh,
    getStepStatus,
    isStepLocked
  };
}
